import { useCallback, useEffect, useRef, useState } from "react";
import { Search, Warehouse, ArrowRight, Trash2, RotateCcw, Play, CheckCircle2, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { beep, fmtDT } from "@/lib/sound";

const inputCls = "w-full bg-white border border-[#D9E3DC] rounded-md px-3 py-2 text-sm text-[#0F2A1F] focus:outline-none focus:ring-2 focus:ring-[#0F6B3A] placeholder:text-[#8A9A90]";
const STATUS = {
  PENDENTE: ["Pendente", "bg-[#FFF4D6] text-[#9A6B0A]"],
  SEM_ESTOQUE: ["Sem estoque", "bg-[#FEE2E2] text-[#B91C1C]"],
  NAO_LOCALIZADO: ["Não localizado", "bg-[#FEE2E2] text-[#B91C1C]"],
  EM_ANDAMENTO: ["Em andamento", "bg-[#D3F2DF] text-[#0F6B3A]"],
  CONCLUIDA: ["Concluída", "bg-[#D1FAE5] text-[#047857]"],
};

function RepCard({ r, onStatus, onDelete }) {
  const [lbl, cls] = STATUS[r.status] || [r.status, "bg-[#F1F5F2] text-[#5A6B62]"];
  const tid = `rep-${r.id}`;
  const multiPulmao = (r.pulmao_localizacoes?.length || 0) > 1;
  return (
    <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm px-5 py-4 flex flex-col lg:flex-row lg:items-center gap-4" data-testid={tid}>
      <div className="min-w-0 flex-1">
        <div className="font-mono text-sm font-semibold text-[#0F6B3A]">{r.codigo}</div>
        <div className="font-semibold text-[#0F2A1F] truncate">{r.descricao || "—"}</div>
        <div className="font-mono text-[11px] text-[#8A9A90] mt-0.5">{r.created_by} · {fmtDT(r.created_at)}{r.concluido_por ? ` · concluída por ${r.concluido_por}` : r.iniciado_por ? ` · em andamento: ${r.iniciado_por}` : ""}</div>
        <div className="flex flex-wrap gap-1 mt-1">
          {r.lista === "2" && <div className="inline-block bg-[#FFF7ED] border border-[#FED7AA] text-[#C2410C] font-bold text-sm px-2 py-0.5 rounded font-mono" data-testid={`${tid}-qtd`}>Qtd a repor: {r.quantidade_necessaria} un.</div>}
          {r.lista === "1" && <div className="inline-block bg-[#EAF7EF] border border-[#B7E4C7] text-[#0F6B3A] font-bold text-sm px-2 py-0.5 rounded font-mono" data-testid={`${tid}-qtd-cx`}>Qtd a separar: {r.qtd_separar ?? 1} CX</div>}
          {multiPulmao && <div className="inline-block bg-[#DBEAFE] border border-[#93C5FD] text-[#1E40AF] font-bold text-xs px-2 py-0.5 rounded font-mono" data-testid={`${tid}-multi-end`}>⚠ {r.pulmao_localizacoes.length} endereços no pulmão</div>}
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <div className="text-right">
          <div className="text-[9px] font-mono tracking-[0.2em] text-[#8A9A90] uppercase">Pulmão · origem{multiPulmao ? ` (${r.pulmao_localizacoes.length})` : ""}</div>
          {r.pulmao_localizacoes?.length ? r.pulmao_localizacoes.map((l, idx) => (
            <div key={l.endereco} className="font-mono text-sm">
              {multiPulmao && <span className="text-[10px] text-[#1E40AF] font-bold mr-1">{idx + 1}º</span>}
              <span className={`font-bold ${l.saldo <= 0 ? "line-through text-[#B91C1C]" : "text-[#0F6B3A]"}`}>{l.endereco}</span>
              <span className="text-[#8A9A90] text-xs"> · qtd {l.saldo}</span>
            </div>
          )) : <div className="font-mono text-sm text-[#B91C1C] font-bold">Não localizado</div>}
        </div>
        <ArrowRight className="w-4 h-4 text-[#8A9A90]" />
        <div>
          <div className="text-[9px] font-mono tracking-[0.2em] text-[#8A9A90] uppercase">Pick list · destino</div>
          <div className="font-mono text-sm font-bold text-[#0F2A1F]">{r.endereco_destino}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0 lg:pl-4">
        <span className={`px-2.5 py-1 rounded text-[11px] font-bold ${cls}`} data-testid={`${tid}-status`}>{lbl}</span>
        {r.status === "CONCLUIDA" ? (
          <button data-testid={`${tid}-reabrir`} onClick={() => onStatus(r, "PENDENTE")} className="h-9 px-3 border border-[#D9E3DC] rounded-md text-[#0F2A1F] text-sm font-semibold hover:bg-[#F1F5F2] flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" /> Reabrir</button>
        ) : r.status === "EM_ANDAMENTO" ? (
          <button data-testid={`${tid}-concluir`} onClick={() => { if (window.confirm(`Confirmar conclusão da reposição de ${r.codigo} → ${r.endereco_destino}?`)) onStatus(r, "CONCLUIDA"); }} className="h-9 px-3 bg-[#10B981] hover:bg-[#059669] rounded-md text-white text-sm font-semibold flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Concluir</button>
        ) : (
          <button data-testid={`${tid}-iniciar`} onClick={() => onStatus(r, "EM_ANDAMENTO")} className="h-9 px-3 border border-[#0F6B3A]/40 rounded-md text-[#0F6B3A] text-sm font-semibold hover:bg-[#EAF7EF] flex items-center gap-1"><Play className="w-3.5 h-3.5" /> Iniciar</button>
        )}
        <button data-testid={`${tid}-excluir`} onClick={() => { if (window.confirm(`Excluir reposição de ${r.codigo}?`)) onDelete(r); }} className="h-9 w-9 flex items-center justify-center border border-[#D9E3DC] rounded-md text-[#5A6B62] hover:text-[#B91C1C] hover:bg-[#FEF2F2]"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

export default function Reposicao() {
  const [lista, setLista] = useState("1");
  const [items, setItems] = useState([]);
  const [cont, setCont] = useState({
    1: { skus: 0, unidades: 0, ids: [], total: 0 },
    2: { skus: 0, unidades: 0, ids: [], total: 0 },
    onda: { skus: 0, unidades: 0, ids: [], total: 0, listas_ativas: [] },
    racks: [],
  });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ativos");
  const [rack, setRack] = useState("");
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");
  const [loading, setLoading] = useState(true);
  const known = useRef(null);
  const filtersActive = !!(q || rack || de || ate || status !== "ativos");

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [l, c] = await Promise.all([
        api.get("/reposicoes", { params: { lista, status, q: q || undefined, rack: rack || undefined, rack_de: de || undefined, rack_ate: ate || undefined } }),
        api.get("/reposicoes/contadores"),
      ]);
      setItems(l.data); setCont(c.data);
      const ids = new Set([...c.data["1"].ids, ...c.data["2"].ids, ...(c.data.onda?.ids || [])]);
      if (known.current && !filtersActive) {
        const novos = [...ids].filter((id) => !known.current.has(id));
        if (novos.length) { beep("new"); toast.info(`${novos.length} novo(s) item(ns) chegaram na reposição`, { duration: 6000 }); }
      }
      known.current = ids;
    } catch { if (!silent) toast.error("Erro ao carregar reposições"); } finally { setLoading(false); }
  }, [lista, status, q, rack, de, ate, filtersActive]);

  useEffect(() => { const t = setTimeout(() => fetchAll(), 200); return () => clearTimeout(t); }, [fetchAll]);
  useEffect(() => { const t = setInterval(() => fetchAll(true), 15000); return () => clearInterval(t); }, [fetchAll]);

  const onStatus = async (r, s) => {
    try { await api.patch(`/reposicoes/${r.id}/status`, { status: s }); toast.success(s === "CONCLUIDA" ? "Reposição concluída" : s === "PENDENTE" ? "Reposição reaberta" : "Reposição iniciada"); fetchAll(true); }
    catch (err) { toast.error(err.response?.data?.detail || "Erro ao atualizar"); }
  };
  const onDelete = async (r) => { try { await api.delete(`/reposicoes/${r.id}`); toast.success("Item excluído"); fetchAll(true); } catch { toast.error("Erro ao excluir"); } };
  const limpar = async () => {
    if (!window.confirm(`Limpar todos os itens da REPOR ${lista}, inclusive os concluídos?`)) return;
    try { const { data } = await api.delete(`/reposicoes/lista/${lista}`); toast.success(`${data.removidas} item(ns) removidos`); fetchAll(true); } catch { toast.error("Erro ao limpar"); }
  };

  const groups = [];
  items.forEach((r) => {
    const key = r.conferencia_lista || "—";
    const g = groups.find((x) => x.lista === key);
    if (g) g.items.push(r); else groups.push({ lista: key, items: [r] });
  });
  const tabCls = (on) => `px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${on ? "bg-[#2DC26B] text-white" : "text-[#0F2A1F] hover:bg-[#F1F5F2]"}`;

  return (
    <div className="space-y-5" data-testid="reposicao-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <div className="text-[11px] font-mono tracking-[0.2em] text-[#5A6B62] uppercase">Abastecimento</div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F2A1F]">Reposição Hub</h1>
          <p className="text-sm text-[#5A6B62] mt-1">Organizada pela localização no estoque pulmão · atualiza automaticamente.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex border border-[#D9E3DC] rounded-md overflow-hidden bg-white" data-testid="reposicao-tabs">
            <button data-testid="reposicao-tab-1" onClick={() => setLista("1")} className={tabCls(lista === "1")}>REPOR 1 ({cont["1"].skus} SKUs)</button>
            <button data-testid="reposicao-tab-2" onClick={() => setLista("2")} className={tabCls(lista === "2")}>REPOR 2 ({cont["2"].skus} SKUs · {cont["2"].unidades} un)</button>
            <button data-testid="reposicao-tab-onda" onClick={() => setLista("onda")} className={tabCls(lista === "onda")}>🌊 REPOR ONDA ({cont.onda?.skus || 0} SKUs · {cont.onda?.unidades || 0} un)</button>
          </div>
          <span className="font-mono text-xs text-[#5A6B62]" data-testid="reposicao-count">{items.length} item(ns)</span>
          {cont[lista]?.total > 0 && lista !== "onda" && (
            <button data-testid="reposicao-limpar-btn" onClick={limpar} className="bg-white hover:bg-[#FEF2F2] text-[#B91C1C] border border-[#FECACA] font-semibold px-3 py-2 rounded-md flex items-center gap-2 text-sm"><Trash2 className="w-4 h-4" /> Limpar lista</button>
          )}
        </div>
      </div>

      <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-3 grid grid-cols-2 md:grid-cols-12 gap-3">
        <div className="relative col-span-2 md:col-span-5">
          <Search className="w-4 h-4 text-[#8A9A90] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="reposicao-busca-input" value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} pl-9`} placeholder="Buscar por código, descrição ou endereço..." />
        </div>
        <select data-testid="reposicao-status-select" value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} md:col-span-2`}>
          <option value="ativos">Ativos (não concluídos)</option><option value="PENDENTE">Pendente</option><option value="SEM_ESTOQUE">Sem estoque</option><option value="NAO_LOCALIZADO">Não localizado</option><option value="EM_ANDAMENTO">Em andamento</option><option value="CONCLUIDA">Concluída</option><option value="todos">Todos</option>
        </select>
        <select data-testid="reposicao-rack-select" value={rack} onChange={(e) => { setRack(e.target.value); setDe(""); setAte(""); }} className={`${inputCls} md:col-span-2`}>
          <option value="">Todos racks</option>{cont.racks.map((r) => <option key={r} value={r}>Rack {r}</option>)}
        </select>
        <input data-testid="reposicao-rack-de" value={de} onChange={(e) => { setDe(e.target.value); setRack(""); }} className={`${inputCls} md:col-span-1`} placeholder="Rack de" inputMode="numeric" />
        <div className="flex items-center gap-1 md:col-span-2"><span className="text-xs text-[#8A9A90]">até</span>
          <input data-testid="reposicao-rack-ate" value={ate} onChange={(e) => { setAte(e.target.value); setRack(""); }} className={inputCls} placeholder="230" inputMode="numeric" /></div>
      </div>

      {loading ? <div className="py-16 text-center text-[#8A9A90]">Carregando...</div>
        : items.length === 0 ? (
          <div className="bg-white border border-[#D9E3DC] rounded-lg py-16 text-center" data-testid="reposicao-empty">
            <div className="text-[#0F2A1F] font-semibold">Nenhum item na REPOR {lista}.</div>
            <div className="text-xs text-[#8A9A90] mt-1">{lista === "1" ? "Produtos marcados como REPOR na Linha de Separação aparecem aqui automaticamente (1 SKU por produto)." : "Produtos marcados com QTD na Linha de Separação aparecem aqui com a quantidade informada."}</div>
          </div>
        ) : (
          <div className="space-y-5" data-testid="reposicao-grupos">
            {groups.map((g) => (
              <div key={g.lista} className="space-y-2">
                <div className="flex items-center gap-2 font-mono text-xs font-bold tracking-[0.2em] text-[#0F2A1F] uppercase"><ClipboardList className="w-4 h-4 text-[#0F6B3A]" /> Da Linha de Separação {g.lista} <span className="text-[#8A9A90] font-normal tracking-normal">{g.items.length} item(ns) · ordem do picklist</span></div>
                {g.items.map((r) => <RepCard key={r.id} r={r} onStatus={onStatus} onDelete={onDelete} />)}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
